import { Table, addToast, showDialogInfo } from '@octano/global-ui';
import { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../../locales/translations';
import { useTutorAssignmentLoader } from './Loaders/TutorAssignmentLoader';
import { useColumns } from '../hooks/useColumns';
import SearchControls from './SearchControls';
import AssignTutorModal, { AssignTutorModalMethods } from './AssignTutorModal';
import { downloadBaseInformation, downloadTemplate } from '../api';
import { downloadFromBlob } from '../../../../utils/blob';
import { TutorAssignmentFilters } from '../types';
import TutorAssignmentDownloading, {
  TutorAssignmentDownloadingMethods,
} from './TutorAssignmentDownloading';

const TutorAssignmentTable = () => {
  const { t } = useTranslation(
    TranslationsKeys.TUTOR_MANAGEMENT_TUTOR_ASSIGNMENT,
  );
  const uploadRef = useRef<AssignTutorModalMethods>(null);
  const downloadingRef = useRef<TutorAssignmentDownloadingMethods>(null);

  const { data, loading } = useTutorAssignmentLoader();

  const [downloading, setDownloading] = useState<boolean>(false);

  const openUpload = useCallback(() => {
    if (loading) {
      return;
    }
    uploadRef?.current?.open();
  }, [loading]);

  const columns = useColumns({ onPressUpload: openUpload });

  const handleDownloadFile = useCallback(
    async (
      key: string,
      filters: TutorAssignmentFilters,
      action: (filters: TutorAssignmentFilters) => Promise<any>,
    ) => {
      if (downloading) {
        return;
      }
      downloadingRef?.current?.open(key);
      setDownloading(true);
      const { data, error, status } = await action(filters);
      setDownloading(false);
      downloadingRef?.current?.close();

      if (status === 204) {
        showDialogInfo({
          icon: {
            name: 'information',
            color: 'primary',
          },
          title: t(`downloading.empty.title`),
          subtitle: t(`downloading.empty.description`),
          btnConfirm: {
            text: t(`downloading.empty.confirm`),
            color: 'primary',
            onConfirm: () => {},
          },
        });
      } else if (data && !error) {
        const blob = new Blob([data], { type: 'vnd.ms-excel' });
        downloadFromBlob(blob, `${t(`downloading.${key}.filename`)}.xlsx`);
      } else {
        addToast({
          icon: 'error',
          color: 'danger',
          text: t(`downloading.${key}.error`),
        });
      }
    },
    [downloading, t],
  );

  return (
    <>
      <div className="mx-3">
        <Card className="p-4">
          <Row>
            <Col className="mb-4" xs={12}>
              <div className="d-flex flex-wrap w-100 align-items-center justify-content-center">
                <div>
                  <p className="fs-14">{t(`description`)}</p>
                </div>
                <div className="flex-fill px-2" />
              </div>
            </Col>
            <Col className="mb-4" xs={12}>
              <SearchControls
                {...data}
                loading={loading}
                onDownloadBase={(filters) =>
                  handleDownloadFile('base', filters, downloadBaseInformation)
                }
                onDownloadTemplate={(filters) =>
                  handleDownloadFile('template', filters, downloadTemplate)
                }
              />
            </Col>
            <Col xs={12}>
              <Table
                data={[{ template: t('table.template') }]}
                columns={columns}
              />
            </Col>
          </Row>
        </Card>
      </div>
      <AssignTutorModal ref={uploadRef} />
      <TutorAssignmentDownloading ref={downloadingRef} />
    </>
  );
};

export default TutorAssignmentTable;
